import React, { useContext, useState } from 'react';

import { Avatar, Box, Button, Typography, useTheme } from '@material-ui/core';
import { AiOutlinePlus } from 'react-icons/ai';
import { TbPencil, TbTrash } from 'react-icons/tb';
import { RiEyeLine } from 'react-icons/ri';
import { Theme } from '@material-ui/core/styles';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import clsx from 'clsx';

import { Company, COMPANY_PERMISSIONS, CompanyPosition } from '@vyce/core/src/types';
import { hasCompanyPermission } from '@vyce/core/src/utils/permissions';
import { AppIconButton } from '@vyce/core/src/components/AppIconButton';
import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { createCompanyWithOwnerRequest, removeStaffMemberRequest } from '@vyce/core/src/api/companies';
import defaultCompanyPicture from '@vyce/core/src/assets/company-icon.png';

import { useActions, useTypedSelector } from '../../hooks';
import useStyles from './styles';
import { NotificationContext } from '@vyce/core/src/contexts/notificationContext';
import { ConfirmDialog } from '@vyce/core/src/components/ConfirmDialog';
import { AddPositionDialog } from './components/AddPositionDialog';
import { TransferOwnershipDialog } from './components/TransferOwnershipDialog';

export const EmployerPositions: React.FC = () => {
  const {
    user,
    helper: { selectedPosition },
  } = useTypedSelector(state => state);
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const [open, setOpen] = useState<boolean>(false);
  const [openTransferOwnershipDialog, setOpenTransferOwnershipDialog] = useState<boolean>(false);
  const [openRemovePositionDialog, setOpenRemovePositionDialog] = useState<boolean>(false);
  const [loadingRemovePosition, setLoadingRemovePosition] = useState<boolean>(false);
  const [positionToRemove, setPositionToRemove] = useState<CompanyPosition>();
  const [addPositionLoading, setAddPositionLoading] = useState<boolean>(false);

  const methods = useForm<any>({
    defaultValues: {
      company: undefined,
      role: '',
    },
  });
  const { getValues, reset } = methods;
  const { setPosition, me } = useActions();
  const history = useHistory();
  const { isMobile } = useContext(DeviceContext);

  const addPosition = async () => {
    try {
      setAddPositionLoading(true);
      const formData = getValues();
      await createCompanyWithOwnerRequest({
        role: formData.role,
        company: {
          name: formData.company.name,
          reg_number: formData.company.registration_number,
          status: formData.company.status,
          address: formData.company.address,
        },
      });
      me();
      showNotification({ message: 'Position has been created', options: { variant: 'success' } });
      reset();
      setOpen(false);
    } catch (e: any) {
      handleServerError(e);
      if (e?.response?.data?.detail) {
        reset();
        setOpen(false);
      }
    } finally {
      setAddPositionLoading(false);
    }
  };

  const select = (position: CompanyPosition) => {
    setPosition(position);
  };

  const goToCompany = (company: Company, path: string = '') => {
    if (!company?.uuid) {
      return;
    }
    history.push(`/companies/${company.name}_${company.uuid}/${path}`);
  };

  const handleRemovePosition = async (position: CompanyPosition) => {
    if (!position.company.uuid || !user.uuid) {
      return;
    }
    try {
      setLoadingRemovePosition(true);
      await removeStaffMemberRequest(position.company.uuid, user.uuid);
      setOpenRemovePositionDialog(false);
      setLoadingRemovePosition(false);
      showNotification({
        message: 'You have successfully removed this company.',
        options: { variant: 'success' },
      });
      const nextPosition = user.positions?.find(p => p.company.uuid !== position.company.uuid);
      if (nextPosition) {
        setPosition(nextPosition);
      }
      me();
    } catch (e) {
      handleServerError(e);
    } finally {
      setLoadingRemovePosition(false);
    }
  };

  return (
    <Box padding={isMobile ? 1 : 5} paddingTop={isMobile ? 3 : 5}>
      <Box className={classes.wrapper}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography className={classes.bold} variant="h6">
            My Companies
          </Typography>

          <Button
            onClick={() => setOpen(true)}
            startIcon={<AiOutlinePlus />}
            variant="contained"
            color="primary">
            Add Position
          </Button>
        </Box>

        {user?.positions?.map(position => (
          <Box
            display="flex"
            justifyContent="space-between"
            flexDirection={isMobile ? 'column' : 'row'}
            alignItems={isMobile ? 'flex-start' : 'center'}
            key={position.company?.uuid}
            marginTop={2}
            onClick={() => select(position)}
            className={clsx(classes.position, {
              [classes.selectedPosition]: selectedPosition?.company?.uuid === position.company?.uuid,
            })}>
            <Box display="flex" alignItems="center">
              <Avatar src={position.company.logo?.url}>
                {!position.company.logo && <img height={40} alt="company" src={defaultCompanyPicture} />}
              </Avatar>
              <Box marginLeft={2}>
                <Typography>{position.company.name}</Typography>
                <Typography variant="caption">{position.role as string}</Typography>
              </Box>
            </Box>

            <Box
              display="flex"
              gridGap={16}
              alignItems="center"
              marginTop={isMobile ? 1 : 0}
              width={isMobile ? '100%' : 'auto'}>
              <AppIconButton
                variant="error"
                onClick={() => {
                  setPositionToRemove(position);
                  // if (position.owner) {
                  //   setOpenTransferOwnershipDialog(true);
                  // } else {
                  //   setOpenRemovePositionDialog(true);
                  // }
                  setOpenRemovePositionDialog(true);
                }}>
                <TbTrash color={theme.palette.error.dark} size="18px" />
              </AppIconButton>

              {hasCompanyPermission(position, COMPANY_PERMISSIONS.CAN_EDIT) && (
                <AppIconButton onClick={() => goToCompany(position?.company, 'general')} variant="secondary">
                  <TbPencil color={theme.palette.secondary.dark} size="18px" />
                </AppIconButton>
              )}

              <AppIconButton variant="primary" onClick={() => goToCompany(position?.company)}>
                <RiEyeLine color={theme.palette.primary.dark} size="18px" />
              </AppIconButton>

              <Button
                style={{ height: '45px' }}
                onClick={() => select(position)}
                size="small"
                fullWidth
                color="secondary"
                variant="contained">
                Select
              </Button>
            </Box>
          </Box>
        ))}
      </Box>

      <AddPositionDialog
        open={open}
        setOpen={setOpen}
        addPosition={addPosition}
        addPositionLoading={addPositionLoading}
        methods={methods}
      />

      <ConfirmDialog
        open={openRemovePositionDialog}
        handleClose={() => setOpenRemovePositionDialog(false)}
        handleConfirm={() => handleRemovePosition(positionToRemove as CompanyPosition)}
        title="Are you sure you want to remove this company?"
        subtitle="If you delete this company from your profile you will no longer have access to its data and will have to be re-invited by that company’s administrator. "
        loading={loadingRemovePosition}
        confirmText="Remove"
        cancelText="Cancel"
      />

      <TransferOwnershipDialog open={openTransferOwnershipDialog} setOpen={setOpenTransferOwnershipDialog} />
    </Box>
  );
};
